import type { OperatorAccount } from '@pf/contracts';
import { KeyRound, PauseCircle, Pencil, Play, PlayCircle, UserPlus, X } from 'lucide-react';
import { useState, type SyntheticEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/auth-context';
import { useCreateOperator, useOperators, useUpdateOperator } from '@/lib/api/ops-queries';
import { formatDateTime } from '@/lib/datetime';
import { cn } from '@/lib/utils';
import { DataTable, type Column } from '@/components/layout/data-table';
import { PageHeader, Section } from '@/components/layout/page';
import { RequireRole } from '@/routes/guards';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Callout, EmptyState, ErrorState, TableSkeleton } from '@/components/ui/feedback';
import {
  Code,
  Input,
  Label,
  Pill,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/primitives';

/**
 * Operators — the console's own accounts: create, change role, suspend, reset passwords.
 *
 * Admin-only including the *read* (`operator:read`), unlike the rest of the console's read
 * surface: the roster of who can approve money movements is itself sensitive.
 *
 * Two rules from the API surface in this UI rather than as surprise 422s:
 *
 * - **No self-modification of role or status.** The editor disables both fields on your own
 *   account. Someone else has to demote or suspend you — which is also what keeps the audit
 *   trail honest.
 * - **No deletion.** Operator rows anchor review decisions and the audit trail, so offboarding
 *   is suspension: the account stops authenticating on its next request, the history stays.
 */
export function OperatorsPage(): ReactNode {
  const { t } = useTranslation();
  return (
    <>
      <PageHeader title={t('operators.title')} description={t('operators.description')} />
      {/* The API 403s every non-admin on the read itself, so explain rather than error. */}
      <RequireRole role="ADMIN">
        <RosterSection />
      </RequireRole>
    </>
  );
}

const ROLES = ['VIEWER', 'MAKER', 'CHECKER', 'ADMIN', 'SUPER'] as const;

function RosterSection(): ReactNode {
  const { t } = useTranslation();
  const operators = useOperators();
  const update = useUpdateOperator();
  const { can, session } = useAuth();
  const myId = session?.operator_id ?? null;
  const [editingId, setEditingId] = useState<string | null>(null);

  const editing = (operators.data?.items ?? []).find((o) => o.id === editingId) ?? null;

  const columns: Column<OperatorAccount>[] = [
    {
      id: 'operator',
      header: t('operators.columns.operator'),
      priority: 'primary',
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">
            {row.display_name}
            {row.id === myId ? (
              <span className="ml-1.5 text-xs text-muted-foreground">{t('operators.you')}</span>
            ) : null}
          </p>
          <Code className="mt-0.5 inline-block">{row.username}</Code>
        </div>
      ),
    },
    {
      id: 'role',
      header: t('operators.columns.role'),
      priority: 'primary',
      cell: (row) => <Pill>{t(`operators.roles.${row.role}`)}</Pill>,
    },
    {
      id: 'status',
      header: t('operators.columns.status'),
      priority: 'secondary',
      cell: (row) => (
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-2xs font-medium',
            row.status === 'ACTIVE'
              ? 'bg-state-good-bg text-state-good'
              : 'bg-state-bad-bg text-state-bad',
          )}
        >
          {row.status === 'ACTIVE' ? (
            <PlayCircle className="size-3" aria-hidden="true" />
          ) : (
            <PauseCircle className="size-3" aria-hidden="true" />
          )}
          {row.status === 'ACTIVE' ? t('operators.statusActive') : t('operators.statusSuspended')}
        </span>
      ),
    },
    {
      id: 'totp',
      header: t('operators.columns.totp'),
      priority: 'detail',
      cell: (row) => (
        <span className="text-xs text-muted-foreground">
          {row.totp_enrolled ? t('operators.totpEnrolled') : t('operators.totpNotEnrolled')}
        </span>
      ),
    },
    {
      id: 'lastLogin',
      header: t('operators.columns.lastSignIn'),
      priority: 'detail',
      cell: (row) => (
        <span className="whitespace-nowrap text-xs text-muted-foreground">
          {row.last_login_at === null ? t('operators.neverSignedIn') : formatDateTime(row.last_login_at)}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      srHeader: t('operators.columns.actionsSrHeader'),
      align: 'right',
      priority: 'secondary',
      cell: (row) => (
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            disabled={!can('ADMIN')}
            onClick={() => {
              setEditingId(row.id === editingId ? null : row.id);
            }}
          >
            <Pencil aria-hidden="true" />
            {t('operators.edit')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={!can('ADMIN') || row.id === myId}
            loading={update.isPending && update.variables?.id === row.id}
            onClick={() => {
              update.mutate({
                id: row.id,
                status: row.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE',
              });
            }}
          >
            {row.status === 'ACTIVE' ? (
              <PauseCircle aria-hidden="true" />
            ) : (
              <Play aria-hidden="true" />
            )}
            {row.status === 'ACTIVE' ? t('operators.suspend') : t('operators.reactivate')}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <Section
        title={t('operators.rosterSectionTitle')}
        description={t('operators.rosterSectionDescription')}
      >
        {operators.isLoading ? (
          <TableSkeleton rows={4} columns={5} />
        ) : operators.isError ? (
          <ErrorState
            error={operators.error}
            onRetry={() => {
              void operators.refetch();
            }}
          />
        ) : (
          <>
            {update.isError ? <ErrorState error={update.error} /> : null}
            <DataTable
              caption={t('operators.tableCaption')}
              columns={columns}
              rows={operators.data?.items ?? []}
              rowKey={(row) => row.id}
              empty={
                <EmptyState
                  title={t('operators.emptyTitle')}
                  description={t('operators.emptyDescription')}
                />
              }
            />
          </>
        )}
      </Section>

      {editing !== null ? (
        <EditSection
          key={editing.id}
          operator={editing}
          isSelf={editing.id === myId}
          onClose={() => {
            setEditingId(null);
          }}
        />
      ) : null}

      <CreateSection />
    </div>
  );
}

function EditSection({
  operator,
  isSelf,
  onClose,
}: {
  operator: OperatorAccount;
  isSelf: boolean;
  onClose: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const update = useUpdateOperator();
  const [displayName, setDisplayName] = useState(operator.display_name);
  const [role, setRole] = useState<OperatorAccount['role']>(operator.role);
  const [status, setStatus] = useState<OperatorAccount['status']>(operator.status);
  const [password, setPassword] = useState('');

  const onSave = (event: SyntheticEvent): void => {
    event.preventDefault();
    if (displayName.trim() === '') return;
    update.mutate(
      {
        id: operator.id,
        ...(displayName.trim() === operator.display_name ? {} : { display_name: displayName.trim() }),
        ...(isSelf || role === operator.role ? {} : { role }),
        ...(isSelf || status === operator.status ? {} : { status }),
        ...(password === '' ? {} : { password }),
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Section title={t('operators.editSectionTitle', { name: operator.display_name })}>
      <Card className="max-w-xl">
        <CardContent className="pt-5">
          <form className="space-y-4" onSubmit={onSave} noValidate>
            {isSelf ? <Callout tone="info">{t('operators.selfEditNotice')}</Callout> : null}
            <div className="space-y-1.5">
              <Label htmlFor="operator-edit-name">{t('operators.nameLabel')}</Label>
              <Input
                id="operator-edit-name"
                value={displayName}
                onChange={(event) => {
                  setDisplayName(event.target.value);
                }}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="operator-edit-role">{t('operators.roleLabel')}</Label>
                <Select
                  value={role}
                  onValueChange={(value) => {
                    setRole(value as OperatorAccount['role']);
                  }}
                  disabled={isSelf}
                >
                  <SelectTrigger id="operator-edit-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((value) => (
                      <SelectItem key={value} value={value}>
                        {t(`operators.roles.${value}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="operator-edit-status">{t('operators.statusLabel')}</Label>
                <Select
                  value={status}
                  onValueChange={(value) => {
                    setStatus(value as OperatorAccount['status']);
                  }}
                  disabled={isSelf}
                >
                  <SelectTrigger id="operator-edit-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">{t('operators.statusActive')}</SelectItem>
                    <SelectItem value="SUSPENDED">{t('operators.statusSuspended')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="operator-edit-password">{t('operators.newPasswordLabel')}</Label>
              <Input
                id="operator-edit-password"
                type="password"
                autoComplete="new-password"
                value={password}
                placeholder={t('operators.newPasswordPlaceholder')}
                onChange={(event) => {
                  setPassword(event.target.value);
                }}
              />
              <p className="text-xs text-muted-foreground">{t('operators.newPasswordHint')}</p>
            </div>
            {update.isError ? <ErrorState error={update.error} /> : null}
            <div className="flex gap-2">
              <Button type="submit" loading={update.isPending}>
                <KeyRound aria-hidden="true" />
                {t('operators.saveButton')}
              </Button>
              <Button type="button" variant="ghost" onClick={onClose}>
                <X aria-hidden="true" />
                {t('operators.cancelButton')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </Section>
  );
}

function CreateSection(): ReactNode {
  const { t } = useTranslation();
  const create = useCreateOperator();
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<(typeof ROLES)[number]>('VIEWER');
  const [password, setPassword] = useState('');

  // Submitting an incomplete form used to `return` in silence — no request, no message, and
  // nothing to tell an admin that the 12-character minimum was the problem.
  const [showInvalid, setShowInvalid] = useState(false);
  const invalid = username.trim() === '' || displayName.trim() === '' || password.length < 8;

  const onCreate = (event: SyntheticEvent): void => {
    event.preventDefault();
    if (invalid) {
      setShowInvalid(true);
      return;
    }
    setShowInvalid(false);
    create.mutate(
      { username: username.trim(), display_name: displayName.trim(), role, password },
      {
        onSuccess: () => {
          setUsername('');
          setDisplayName('');
          setRole('VIEWER');
          setPassword('');
        },
      },
    );
  };

  return (
    <Section
      title={t('operators.createSectionTitle')}
      description={t('operators.createSectionDescription')}
    >
      <Card className="max-w-xl">
        <CardContent className="pt-5">
          <form className="space-y-4" onSubmit={onCreate} noValidate>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="operator-new-username">{t('operators.usernameLabel')}</Label>
                <Input
                  id="operator-new-username"
                  type="text"
                  autoComplete="off"
                  value={username}
                  placeholder={t('operators.usernamePlaceholder')}
                  onChange={(event) => {
                    setUsername(event.target.value);
                  }}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="operator-new-name">{t('operators.nameLabel')}</Label>
                <Input
                  id="operator-new-name"
                  value={displayName}
                  placeholder={t('operators.namePlaceholder')}
                  onChange={(event) => {
                    setDisplayName(event.target.value);
                  }}
                />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="operator-new-role">{t('operators.roleLabel')}</Label>
                <Select
                  value={role}
                  onValueChange={(value) => {
                    setRole(value as (typeof ROLES)[number]);
                  }}
                >
                  <SelectTrigger id="operator-new-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((value) => (
                      <SelectItem key={value} value={value}>
                        {t(`operators.roles.${value}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{t('operators.roleHint')}</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="operator-new-password">{t('operators.passwordLabel')}</Label>
                <Input
                  id="operator-new-password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  placeholder={t('operators.passwordPlaceholder')}
                  onChange={(event) => {
                    setPassword(event.target.value);
                  }}
                />
                <p className="text-xs text-muted-foreground">{t('operators.passwordHint')}</p>
              </div>
            </div>
            {showInvalid && invalid ? (
              <p className="text-destructive text-sm">{t('operators.createValidation')}</p>
            ) : null}
            {create.isError ? <ErrorState error={create.error} /> : null}
            <Button type="submit" loading={create.isPending}>
              <UserPlus aria-hidden="true" />
              {t('operators.createButton')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Section>
  );
}
